import React, { useEffect, useRef, useState } from 'react';
import { Button } from 'react-bootstrap';
import { Bot, Building2, Check, Clock, KeyRound, Plug, RefreshCw, Unplug } from 'lucide-react';
import axios from '../../services/AxiosInstance';
import { getApiUrl } from '../../utils/ApiUtils';
import { scopeLabels } from './McpConsent';
import AccountPage from '../layouts/AccountPage';
import PageHeader from '../layouts/PageHeader';
import EmptyState from '../components/Common/EmptyState';
import StatusBadge from '../components/Common/StatusBadge';
import Callout from '../components/Common/Callout';

const date = value => value ? new Date(value).toLocaleString('sv-SE') : 'Inte registrerat';
const statuses = { connected: 'Ansluten', disconnected: 'Frånkopplad', expired: 'Utgången' };
const tones = { connected: 'success', disconnected: 'muted', expired: 'warning' };

export default function ConnectedApps() {
    const [connections, setConnections] = useState([]), [busy, setBusy] = useState(true), [error, setError] = useState('');
    const [loaded, setLoaded] = useState(false);
    const active = useRef(true);
    const load = async () => {
        setBusy(true); setError('');
        try { const { data } = await axios.get(getApiUrl('auth/mcp/connections')); if (active.current) setConnections(data); }
        catch { if (active.current) setError('Anslutningarna kunde inte hämtas. Försök igen.'); }
        finally { if (active.current) { setBusy(false); setLoaded(true); } }
    };
    useEffect(() => { active.current = true; load(); return () => { active.current = false; }; }, []);
    const disconnect = async id => {
        setBusy(true); setError('');
        try {
            await axios.post(getApiUrl(`auth/mcp/connections/${id}/disconnect`), {});
            if (active.current) await load();
        } catch { if (active.current) { setError('Appen kunde inte kopplas från. Försök igen.'); setBusy(false); } }
    };
    return <AccountPage width="wide">
        <PageHeader icon={Plug} title="Anslutna appar" subtitle="AI-appar som du har gett åtkomst till LedgerLoop."
            actions={<Button variant="outline-primary" size="sm" onClick={load} disabled={busy}>
                <RefreshCw size={14} className={busy ? 'spin' : ''} aria-hidden="true" /> {busy ? 'Hämtar…' : 'Uppdatera'}</Button>} />
        {error && <Callout tone="danger" role="alert"><p>{error}</p></Callout>}
        {loaded && !busy && !error && !connections.length && <EmptyState icon={Plug} title="Inga anslutna appar"
            description="När du godkänner att en AI-app, till exempel Claude eller ChatGPT, får ansluta till LedgerLoop visas den här." />}
        {connections.map(connection => <section className="modern-card" key={connection.id} aria-label={connection.clientName}>
            <div className="modern-card-header">
                <div className="ll-app">
                    <span className="ll-app__glyph"><Bot size={20} strokeWidth={1.75} aria-hidden="true" /></span>
                    <div>
                        <h2 className="ll-app__name">{connection.clientName}</h2>
                        <p className="ll-app__sub">{`Godkänd ${date(connection.createdAt)}`}</p>
                    </div>
                </div>
                <div className="action-buttons">
                    <StatusBadge tone={tones[connection.status] || 'warning'}>{statuses[connection.status] || 'Status behöver kontrolleras'}</StatusBadge>
                    {connection.status === 'connected' && <Button variant="outline-danger" size="sm" disabled={busy} aria-label={`Koppla från ${connection.clientName}`} onClick={() => disconnect(connection.id)}>
                        <Unplug size={14} aria-hidden="true" /> Koppla från</Button>}
                </div>
            </div>
            <div className="modern-card-body">
                <div className="ll-app-grid">
                    <div>
                        <h3 className="ll-consent__heading"><Building2 size={12} aria-hidden="true" /> Företag</h3>
                        {connection.companies.length
                            ? <ul className="ll-pills">{connection.companies.map(company => <li key={company.companyId} className="ll-pill">{company.companyName}</li>)}</ul>
                            : <p className="small text-muted mb-0">Inga företag</p>}
                    </div>
                    <div>
                        <h3 className="ll-consent__heading"><KeyRound size={12} aria-hidden="true" /> Åtkomst</h3>
                        {connection.scopes.length
                            ? <ul className="ll-checklist">{connection.scopes.map(scope => <li key={scope}><Check size={14} aria-hidden="true" />{scopeLabels[scope] || scope}</li>)}</ul>
                            : <p className="small text-muted mb-0">Ingen åtkomst</p>}
                    </div>
                </div>
                <ul className="ll-meta">
                    <li className="ll-meta__item"><Clock size={14} aria-hidden="true" />Senast använd: <strong>{date(connection.lastUsedAt)}</strong></li>
                    <li className="ll-meta__item"><Clock size={14} aria-hidden="true" />Gäller till: <strong>{date(connection.expiresAt)}</strong></li>
                </ul>
            </div>
        </section>)}
        {connections.length > 0 && <p className="account-page__footnote">Frånkoppling stoppar fortsatt åtkomst. Information som appen redan har hämtat finns kvar hos den tjänsten.</p>}
    </AccountPage>;
}
